import { Station } from "./nav_map";
import { t } from "./i18n";
import { reducedMotion } from "./motion_pref";

// the phone dock's tuner. each station sits on a scale under a red needle; drag the scale and it settles on the
// nearest one, passing through static on the way. a tap on a station's frequency tunes straight to it

const STEP = 74;

export interface Tuner {
  show(index: number | null, burst?: boolean, idle?: string): void;
}

function calm(): boolean {
  return reducedMotion();
}

export function createTuner(host: HTMLElement, stations: Station[], onTune: (index: number) => void, onOpen?: () => void): Tuner {
  host.classList.add("tuner");
  host.innerHTML = `<canvas class="tuner-static" width="96" height="24" aria-hidden="true"></canvas>
    <div class="tuner-now" aria-live="polite"><b></b><span></span></div>
    <div class="tuner-scale"><div class="tuner-strip"></div><i class="tuner-needle" aria-hidden="true"></i></div>`;
  const strip = host.querySelector<HTMLElement>(".tuner-strip")!;
  const name = host.querySelector<HTMLElement>(".tuner-now b")!;
  const freq = host.querySelector<HTMLElement>(".tuner-now span")!;
  const canvas = host.querySelector<HTMLCanvasElement>(".tuner-static")!;
  const ink = canvas.getContext("2d");

  strip.replaceChildren(...stations.map((station, index) => {
    const button = document.createElement("button");
    button.type = "button";
    button.textContent = station.freq;
    button.setAttribute("aria-label", `${station.label}, ${station.freq}`);
    button.addEventListener("click", () => {
      if (!moved) onTune(index);
    });
    return button;
  }));

  let current: number | null = 0;
  let offset = 0;
  let staticUntil = 0;
  let frame = 0;

  const centre = (index: number) => host.clientWidth / 2 - (index * STEP + STEP / 2);
  function place(x: number): void {
    offset = x;
    strip.style.transform = `translateX(${x}px)`;
  }
  let idleLabel = "";
  function label(index: number | null): void {
    name.textContent = index === null ? idleLabel || t("Tool") : stations[index].label;
    freq.textContent = index === null ? "" : stations[index].freq;
    [...strip.children].forEach((child, at) => child.classList.toggle("on", at === index));
  }

  function drawStatic(): void {
    if (!ink) return;
    const image = ink.createImageData(canvas.width, canvas.height);
    for (let at = 0; at < image.data.length; at += 4) {
      const v = (Math.random() * 255) | 0;
      image.data[at] = v * 0.6;
      image.data[at + 1] = v * 0.9;
      image.data[at + 2] = v;
      image.data[at + 3] = Math.random() < 0.55 ? 200 : 0;
    }
    ink.putImageData(image, 0, 0);
    if (performance.now() < staticUntil) frame = requestAnimationFrame(drawStatic);
    else canvas.classList.remove("on");
  }
  function burst(ms: number): void {
    if (calm()) return;
    staticUntil = Math.max(staticUntil, performance.now() + ms);
    if (canvas.classList.contains("on")) return;
    canvas.classList.add("on");
    cancelAnimationFrame(frame);
    drawStatic();
  }

  // drag the scale sideways; a short move is still a tap
  let moved = false;
  let down = false;
  let startX = 0;
  let startOffset = 0;
  let pointer = 0;
  host.addEventListener("pointerdown", (event) => {
    down = true;
    moved = false;
    startX = event.clientX;
    startOffset = offset;
    pointer = event.pointerId;
  });
  host.addEventListener("pointermove", (event) => {
    if (!down) return;
    const dx = event.clientX - startX;
    if (!moved && Math.abs(dx) > 6) {
      moved = true;
      host.setPointerCapture(pointer);
      strip.classList.add("drag");
    }
    if (!moved) return;
    const min = centre(stations.length - 1);
    const max = centre(0);
    place(Math.max(min, Math.min(max, startOffset + dx)));
    const exact = (centre(0) - offset) / STEP;
    const near = Math.round(exact);
    if (Math.abs(exact - near) > 0.25) {
      burst(90);
      name.textContent = "· · ·";
      freq.textContent = "";
      [...strip.children].forEach((child) => child.classList.remove("on"));
    } else {
      label(near);
    }
  });
  const release = () => {
    if (!down) return;
    down = false;
    strip.classList.remove("drag");
    if (!moved) return;
    const near = Math.max(0, Math.min(stations.length - 1, Math.round((centre(0) - offset) / STEP)));
    onTune(near);
    if (near === current) show(near);
    window.setTimeout(() => {
      moved = false;
    }, 0);
  };
  // a plain tap on the readout (not on a frequency, not after a drag) opens the station sheet
  host.addEventListener("click", (event) => {
    if (!moved && onOpen && !(event.target as Element).closest(".tuner-strip button")) onOpen();
  });
  host.addEventListener("pointerup", release);
  host.addEventListener("pointercancel", release);
  host.addEventListener("keydown", (event) => {
    const at = current ?? 0;
    if (event.key === "ArrowRight" && at < stations.length - 1) onTune(at + 1);
    else if (event.key === "ArrowLeft" && at > 0) onTune(at - 1);
  });
  new ResizeObserver(() => {
    if (current !== null) place(centre(current));
  }).observe(host);

  function show(index: number | null, withBurst = false, idle = t("Tool")): void {
    idleLabel = idle;
    const changed = index !== current;
    current = index;
    if (index !== null) place(centre(index));
    label(index);
    if (changed && withBurst) burst(260);
  }

  return { show };
}
